import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API_URL } from "../../env";

/**
 * Fetches and normalizes product categories from /view.
 * Both ProductsSection (full catalog page) and SolutionsGrid (homepage
 * teaser) were duplicating this fetch/sort logic — centralizing it means
 * a fix here fixes both, and a failed request is always visible as an
 * explicit "error" status instead of silently rendering nothing.
 */
export default function useProductCategories() {
  const [categories, setCategories] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | error | empty | ready
  const requestId = useRef(0);

  const fetchCategories = useCallback(() => {
    const id = ++requestId.current;
    setStatus("loading");

    return axios
      .get(`${API_URL}/view`)
      .then((res) => {
        if (requestId.current !== id) return; // a newer request superseded this one

        const normalized = [...(res.data ?? [])]
          .sort((a, b) => a.order - b.order)
          .map((category) => ({
            ...category,
            products: [...(category.products ?? [])].sort(
              (a, b) => a.order - b.order,
            ),
          }));

        setCategories(normalized);
        setStatus(normalized.length === 0 ? "empty" : "ready");
      })
      .catch(() => {
        if (requestId.current !== id) return;
        setStatus("error");
      });
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return { categories, status, refetch: fetchCategories };
}